import { Modal } from 'bootstrap';

import { ButtonVariant } from '@/shared/types/button';

declare global {
  interface Window {
    alertModal: typeof alertModal;
    confirmModal: typeof confirmModal;
    promptModal: typeof promptModal;
  }
}

function getModalElements() {
  const modal = document.getElementById('modal')!;
  return {
    modal,
    dialog: modal.querySelector('.modal-dialog')!,
    title: modal.querySelector('.modal-title')!,
    body: modal.querySelector('.modal-body')!,
    footer: modal.querySelector('.modal-footer')!,
  };
}

export function showModal(options: { title: string; body: string; fullScreen?: boolean }) {
  const el = getModalElements();

  const modal = new Modal(el.modal);

  el.dialog.classList.toggle('modal-fullscreen', !!options?.fullScreen);

  el.title.innerHTML = options.title;
  el.body.innerHTML = options.body;

  el.footer.classList.add('d-none');
  el.footer.replaceChildren();

  modal.show();
}

export async function alertModal(message: string, variant: ButtonVariant = 'primary'): Promise<void> {
  if (window.parent !== window) {
    await window.parent.alertModal(message, variant);
    return;
  }

  return new Promise((resolve) => {
    const el = getModalElements();

    const modal = new Modal(el.modal);

    el.dialog.classList.remove('modal-fullscreen');

    el.title.textContent = 'Alert';
    el.body.textContent = message;

    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'OK';
    button.className = `btn btn-${variant}`;
    button.addEventListener('click', () => modal.hide(), { once: true });

    el.footer.classList.remove('d-none');
    el.footer.replaceChildren(button);

    el.modal.addEventListener('hidden.bs.modal', () => resolve(), { once: true });

    modal.show();
  });
}

export async function confirmModal(
  message: string,
  confirmButtonLabel: string = 'OK',
  variant: ButtonVariant = 'primary',
  cancelButtonLabel: string = 'Cancel',
): Promise<boolean> {
  if (window.parent !== window) {
    return await window.parent.confirmModal(message, confirmButtonLabel, variant, cancelButtonLabel);
  }

  return new Promise((resolve) => {
    const el = getModalElements();

    const modal = new Modal(el.modal);

    el.dialog.classList.remove('modal-fullscreen');

    el.title.textContent = 'Confirm';
    el.body.innerHTML = message;

    let settled = false;
    const resolveOnce = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.textContent = cancelButtonLabel;
    cancelButton.className = `btn btn-secondary`;
    cancelButton.addEventListener(
      'click',
      () => {
        resolveOnce(false);
        modal.hide();
      },
      { once: true },
    );

    const confirmButton = document.createElement('button');
    confirmButton.type = 'button';
    confirmButton.textContent = confirmButtonLabel;
    confirmButton.className = `btn btn-${variant}`;
    confirmButton.addEventListener(
      'click',
      () => {
        resolveOnce(true);
        modal.hide();
      },
      { once: true },
    );

    el.footer.classList.remove('d-none');
    el.footer.replaceChildren(cancelButton, confirmButton);

    el.modal.addEventListener('hidden.bs.modal', () => resolveOnce(false), { once: true });

    modal.show();
  });
}

export async function promptModal(options: {
  title?: string;
  message: string;
  confirmButtonLabel?: string;
  variant?: ButtonVariant;
  maxLength?: number;
}): Promise<string | null> {
  const { title = 'Confirm', message, confirmButtonLabel = 'OK', variant = 'primary', maxLength } = options;

  if (window.parent !== window) {
    return await window.parent.promptModal(options);
  }

  return new Promise((resolve) => {
    const el = getModalElements();

    const modal = new Modal(el.modal);

    el.dialog.classList.remove('modal-fullscreen');

    el.title.textContent = title;

    const wrapper = document.createElement('div');
    wrapper.className = 'd-flex flex-column gap-1';

    const label = document.createElement('label');
    label.className = 'form-label mb-0';
    label.htmlFor = 'promptModalInput';
    label.textContent = message;

    const textarea = document.createElement('textarea');
    textarea.id = 'promptModalInput';
    textarea.className = 'form-control';
    textarea.rows = 3;
    if (maxLength) textarea.maxLength = maxLength;

    wrapper.append(label, textarea);
    el.body.replaceChildren(wrapper);

    let settled = false;
    const resolveOnce = (value: string | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.textContent = 'Cancel';
    cancelButton.className = 'btn btn-secondary';
    cancelButton.addEventListener(
      'click',
      () => {
        resolveOnce(null);
        modal.hide();
      },
      { once: true },
    );

    const confirmButton = document.createElement('button');
    confirmButton.type = 'button';
    confirmButton.textContent = confirmButtonLabel;
    confirmButton.className = `btn btn-${variant}`;
    confirmButton.disabled = true;
    confirmButton.addEventListener('click', () => {
      const reason = textarea.value.trim();
      if (!reason) return;

      resolveOnce(maxLength ? reason.slice(0, maxLength) : reason);
      modal.hide();
    });

    textarea.addEventListener('input', () => {
      confirmButton.disabled = !textarea.value.trim();
    });

    el.footer.classList.remove('d-none');
    el.footer.replaceChildren(cancelButton, confirmButton);

    el.modal.addEventListener('hidden.bs.modal', () => resolveOnce(null), { once: true });
    el.modal.addEventListener('shown.bs.modal', () => textarea.focus(), { once: true });

    modal.show();
  });
}

window.alertModal = alertModal;
window.confirmModal = confirmModal;
window.promptModal = promptModal;
